import { useCallback } from 'react';
import useSWR, { preload } from 'swr';
import {
  listOrgInvitesApiv2,
  createOrgInviteApi,
  cancelOrgInviteApi,
  resendOrgInviteApi,
  acceptOrgInviteApi,
  declineOrgInviteApi,
} from 'src/api/org-invites';
import type { OrgInvite } from '@equaliq/api-model';
import { InviteStatus } from '@equaliq/api-model';

const fetcher = async (orgId: string) => {
  const response = await listOrgInvitesApiv2({ orgId });
  return Object.values(response.invites ?? {}) as OrgInvite[];
};

export const preloadOrgInvites = (orgId: string) =>
  preload(['invites', orgId], () => fetcher(orgId)).catch(() => undefined);

export const useOrgInvites = (orgId?: string) => {
  const swrKey = orgId ? ['invites', orgId] : null;

  const { data, error, isLoading, mutate } = useSWR(swrKey, () => fetcher(orgId!));

  const invites = data ?? [];

  const refreshInvites = async () => {
    await mutate();
  };

  const createInvite = useCallback(async (
    email: string,
    role?: string,
    customRoleId?: string
  ) => {
    await createOrgInviteApi({
      orgId: orgId!,
      emails: [email],
      role: role || 'member',
      customRoleId,
    });
    await mutate();
  }, [orgId, mutate]);

  const cancelInvite = useCallback(async (inviteId: string) => {
    await mutate(
      invites.filter(i => i.orgInviteId !== inviteId),
      false
    );

    try {
      await cancelOrgInviteApi({ orgId: orgId!, inviteId });
      await mutate();
    } catch (err) {
      await mutate();
      throw err;
    }
  }, [orgId, invites, mutate]);

  const resendInvite = useCallback(async (inviteId: string) => {
    await resendOrgInviteApi({ orgId: orgId!, inviteId });
  }, [orgId]);

  const acceptInvite = useCallback(async (inviteId: string) => {
    await mutate(
      invites.map(i =>
        i.orgInviteId === inviteId ? { ...i, status: InviteStatus.accepted } : i
      ),
      false
    );

    try {
      await acceptOrgInviteApi({ orgId: orgId!, inviteId });
      await mutate();
    } catch (err) {
      await mutate();
      throw err;
    }
  }, [orgId, invites, mutate]);

  const declineInvite = useCallback(async (inviteId: string) => {
    await mutate(
      invites.map(i =>
        i.orgInviteId === inviteId ? { ...i, status: InviteStatus.declined } : i
      ),
      false
    );

    try {
      await declineOrgInviteApi({ orgId: orgId!, inviteId });
      await mutate();
    } catch (err) {
      await mutate();
      throw err;
    }
  }, [orgId, invites, mutate]);

  return {
    invites,
    isLoading,
    error,
    refreshInvites,
    createInvite,
    cancelInvite,
    resendInvite,
    acceptInvite,
    declineInvite,
  };
};
